import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RefreshCw, Search, TrendingUp, X } from 'lucide-react';
import {
  getASOExplore,
  getASOFilters,
  getASORuns,
  getASOTimeline,
  triggerASOSync,
  type ASOExploreResponse,
  type ASOFiltersResponse,
  type ASOTermRow,
  type ASOTimelineResponse,
} from '../api/client';
import { AppError } from '../api/request';
import { useError } from '../context/ErrorContext';
import { panelPath } from '../config/paths';
import PageToolbar from './PageToolbar';
import {
  countryFlag,
  DeltaBadge,
  formatGenreLabel,
  PopularityBar,
  Popularity5Badge,
  Sparkline,
} from './asoUI';

const SEGMENTS = ['rising', 'top', 'new', 'falling'] as const;
type Segment = (typeof SEGMENTS)[number];
const TIERS = ['t1', 't2', 't3', 't4'] as const;

/* ASO keşif ekranı — pazar seviyesi keyword verisi (Apple Ads Platform API v1).
 *
 * Varsayılan sekme bilerek "top" değil "rising": statik top-500 listesi her ASO aracında var,
 * bizim farkımız sakladığımız timeline'dan gelen trend. */
const ASOKeywordsPage: React.FC = () => {
  const { t } = useTranslation();
  const { showError } = useError();

  const [filters, setFilters] = useState<ASOFiltersResponse | null>(null);
  const [tier, setTier] = useState<string>('t1');
  const [country, setCountry] = useState<string>('');
  const [genre, setGenre] = useState<string>('');
  const [period, setPeriod] = useState<string>('');
  const [segment, setSegment] = useState<Segment>('rising');

  const [data, setData] = useState<ASOExploreResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncBanner, setSyncBanner] = useState<{ kind: 'info' | 'error'; message: string; code?: string } | null>(null);
  const [search, setSearch] = useState('');

  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<ASOTimelineResponse | null>(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Filtre listeleri toplanan veriden gelir — Apple sabit bir genre enum'u yayınlamıyor.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const f = await getASOFilters();
        if (cancelled) return;
        setFilters(f);
        const tierCountries = f.tiers?.[tier] ?? [];
        const nextCountry = tierCountries[0] ?? f.countries[0] ?? '';
        setCountry(prev => prev || nextCountry);
      } catch (e) {
        if (!cancelled) showError(e instanceof AppError ? e.userMessage : t('aso.loadError'));
      }
    })();
    return () => {
      cancelled = true;
    };
    // tier değişimini ayrı efekt yönetir; bu efekt yalnızca ilk yüklemede çalışmalı
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ülke değişince o ülkede gerçekten veri bulunan genre listesini tazele.
  useEffect(() => {
    if (!country) return;
    let cancelled = false;
    (async () => {
      try {
        const f = await getASOFilters({ country });
        if (cancelled) return;
        setFilters(prev => (prev ? { ...prev, genres: f.genres, periods: f.periods } : f));
        setGenre(prev => (prev && f.genres.includes(prev) ? prev : f.genres[0] ?? ''));
      } catch (e) {
        if (!cancelled) showError(e instanceof AppError ? e.userMessage : t('aso.loadError'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [country, showError, t]);

  const loadExplore = useCallback(async () => {
    if (!country || !genre) return;
    setLoading(true);
    try {
      const res = await getASOExplore({ country, genre, segment, period: period || undefined, limit: 100 });
      setData(res);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('aso.loadError'));
    } finally {
      setLoading(false);
    }
  }, [country, genre, segment, period, showError, t]);

  useEffect(() => {
    void loadExplore();
  }, [loadExplore]);

  // Sayfa açılışında son toplama koşusunu kontrol et — hâlâ devam eden bir hata varsa
  // (ör. bağlı Apple Search Ads hesabı yok) kullanıcı "Collect now"a basmadan önce görsün.
  useEffect(() => {
    (async () => {
      try {
        const { runs } = await getASORuns(1);
        const latest = runs[0];
        if (latest?.status === 'errored' && latest.errorMessage) {
          setSyncBanner({ kind: 'error', message: latest.errorMessage, code: latest.errorCode });
        }
      } catch {
        // sessizce yut — ana sayfa yükleme hatası zaten ayrı ele alınıyor
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Toplama arka planda çalışır (bkz. ASOService.TriggerSync); tetikleme isteği hemen döner.
  // Gerçek sonucu görmek için koşu tamamlanana kadar kısa aralıklarla kontrol ederiz.
  const pollLatestRun = (attemptsLeft: number) => {
    getASORuns(1)
      .then(({ runs }) => {
        const latest = runs[0];
        if (!latest) return;
        if (latest.status === 'running') {
          if (attemptsLeft > 0) setTimeout(() => pollLatestRun(attemptsLeft - 1), 4000);
          return;
        }
        if (latest.status === 'errored') {
          setSyncBanner({ kind: 'error', message: latest.errorMessage || t('aso.syncError'), code: latest.errorCode });
          return;
        }
        setSyncBanner(null);
        void loadExplore();
      })
      .catch(() => {
        // sessizce yut — bu yalnızca ilerleme takibi, kullanıcıya ayrıca hata göstermeye değmez
      });
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncBanner(null);
    try {
      await triggerASOSync();
      setSyncBanner({ kind: 'info', message: t('aso.syncStarted') });
      pollLatestRun(8);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('aso.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  const openTerm = async (term: string) => {
    setSelectedTerm(term);
    setLoadingTimeline(true);
    setTimeline(null);
    try {
      const tl = await getASOTimeline({ country, term, genre, periods: 26 });
      setTimeline(tl);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('aso.loadError'));
    } finally {
      setLoadingTimeline(false);
    }
  };

  const tierCountries = useMemo(() => filters?.tiers?.[tier] ?? [], [filters, tier]);

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const q = search.trim().toLowerCase();
    return q ? all.filter(r => r.searchTerm.toLowerCase().includes(q)) : all;
  }, [data, search]);

  const hasNoData = !loading && (!filters || filters.countries.length === 0);

  return (
    <div className="p-4 space-y-4">
      <PageToolbar standalone>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{t('aso.title')}</h1>
        <button
          type="button"
          onClick={handleSync}
          disabled={syncing}
          className="ml-auto inline-flex items-center gap-2 h-8 px-3 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{t('aso.syncNow')}</span>
        </button>
      </PageToolbar>

      {syncBanner && (
        <div
          className={
            syncBanner.kind === 'error'
              ? 'flex flex-wrap items-center gap-3 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-200'
              : 'rounded-lg border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-800 dark:text-blue-200'
          }
        >
          <span>{syncBanner.message}</span>
          {syncBanner.code === 'aso.noCredentials' && (
            <Link
              to={panelPath('/apple-search-ads')}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white px-3 py-1.5 text-xs font-semibold whitespace-nowrap"
            >
              {t('appleSearchAds.heading')}
            </Link>
          )}
        </div>
      )}

      {hasNoData ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
          <TrendingUp className="w-8 h-8 mx-auto text-gray-400 mb-3" />
          <p className="text-sm font-medium text-gray-900 dark:text-white">{t('aso.emptyTitle')}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('aso.emptyBody')}</p>
        </div>
      ) : (
        <>
          {/* Filtre çubuğu: tier → ülke → genre → dönem */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={tier}
              onChange={e => {
                const nextTier = e.target.value;
                setTier(nextTier);
                const list = filters?.tiers?.[nextTier] ?? [];
                if (list.length) setCountry(list[0]);
              }}
              className="h-8 px-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200"
            >
              {TIERS.map(tr => (
                <option key={tr} value={tr}>
                  {t(`aso.tier.${tr}`)}
                </option>
              ))}
            </select>

            <select
              value={country}
              onChange={e => setCountry(e.target.value)}
              className="h-8 px-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200"
            >
              {(tierCountries.length ? tierCountries : filters?.countries ?? []).map(c => (
                <option key={c} value={c}>
                  {countryFlag(c)} {c}
                </option>
              ))}
            </select>

            <select
              value={genre}
              onChange={e => setGenre(e.target.value)}
              className="h-8 px-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200"
            >
              {(filters?.genres ?? []).map(g => (
                <option key={g} value={g}>
                  {formatGenreLabel(g)}
                </option>
              ))}
            </select>

            <select
              value={period}
              onChange={e => setPeriod(e.target.value)}
              className="h-8 px-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200"
            >
              <option value="">{t('aso.latestPeriod')}</option>
              {(filters?.periods ?? []).map(p => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            <div className="relative ml-auto">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('aso.searchPlaceholder')}
                className="h-8 pl-7 pr-2 w-44 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200"
              />
            </div>
          </div>

          {/* Segment sekmeleri — sayılar hangi sekmede iş olduğunu önden gösterir */}
          <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
            {SEGMENTS.map(seg => (
              <button
                key={seg}
                type="button"
                onClick={() => setSegment(seg)}
                className={`px-3 py-2 text-sm -mb-px border-b-2 ${
                  segment === seg
                    ? 'border-gray-900 dark:border-white font-medium text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {t(`aso.segment.${seg}`)}
                {data?.counts?.[seg] ? (
                  <span className="ml-1.5 text-xs text-gray-400 tabular-nums">{data.counts[seg]}</span>
                ) : null}
              </button>
            ))}
          </div>

          <ASOTermTable rows={rows} loading={loading} onSelect={openTerm} />
        </>
      )}

      {selectedTerm && (
        <ASOTermDrawer
          term={selectedTerm}
          country={country}
          timeline={timeline}
          loading={loadingTimeline}
          onClose={() => setSelectedTerm(null)}
        />
      )}
    </div>
  );
};

/** Keşif tablosu. Trend her satırda görünür — ürünün varlık sebebi bu. */
const ASOTermTable: React.FC<{ rows: ASOTermRow[]; loading: boolean; onSelect: (term: string) => void }> = ({
  rows,
  loading,
  onSelect,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">{t('common.loading')}</p>;
  }
  if (!rows.length) {
    return <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">{t('aso.noRows')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-gray-400 dark:text-gray-500">
            <th className="text-left font-normal py-2 pr-2 w-10">#</th>
            <th className="text-left font-normal py-2 pr-2">{t('aso.term')}</th>
            <th className="text-left font-normal py-2 pr-2 w-28">{t('aso.popularity')}</th>
            <th className="text-center font-normal py-2 pr-2 w-10">1–5</th>
            <th className="text-right font-normal py-2 pr-2 w-20">{t('aso.deltaWeek')}</th>
            <th className="text-right font-normal py-2 w-20">{t('aso.trend')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr
              key={row.searchTerm}
              onClick={() => onSelect(row.searchTerm)}
              className="border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              <td className="py-2.5 pr-2 text-gray-400 tabular-nums">{row.rankInGenre}</td>
              <td className="py-2.5 pr-2 text-gray-900 dark:text-gray-100">{row.searchTerm}</td>
              <td className="py-2.5 pr-2">
                <PopularityBar value={row.popularity} />
              </td>
              <td className="py-2.5 pr-2 text-center">
                <Popularity5Badge value={row.popularity1to5} />
              </td>
              <td className="py-2.5 pr-2 text-right">
                <DeltaBadge delta={row.popularityDelta} isNew={row.isNew} />
              </td>
              <td className="py-2.5 text-right">
                <Sparkline points={row.sparkline} delta={row.popularityDelta} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Terim detay paneli: 26 haftalık popülerlik seyri. */
const ASOTermDrawer: React.FC<{
  term: string;
  country: string;
  timeline: ASOTimelineResponse | null;
  loading: boolean;
  onClose: () => void;
}> = ({ term, country, timeline, loading, onClose }) => {
  const { t } = useTranslation();
  const points = timeline?.points ?? [];

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-md h-full bg-white dark:bg-gray-800 shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-2">
          <div className="min-w-0">
            <p className="font-medium text-gray-900 dark:text-white truncate">{term}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {countryFlag(country)} {country}
              {timeline?.tier ? ` · ${t(`aso.tier.${timeline.tier}`)}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="ml-auto p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          ) : points.length < 2 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('aso.timelineTooShort')}</p>
          ) : (
            <>
              <TimelineChart points={points} />
              <div className="space-y-1">
                {points
                  .slice()
                  .reverse()
                  .slice(0, 12)
                  .map(p => (
                    <div
                      key={p.period}
                      className="flex items-center gap-3 text-sm py-1 border-b border-gray-50 dark:border-gray-700/50"
                    >
                      <span className="text-xs text-gray-400 tabular-nums w-20">{p.period}</span>
                      <span className="text-xs text-gray-400 tabular-nums w-10">#{p.rankInGenre}</span>
                      <PopularityBar value={p.searchPopularity1to100} className="flex-1" />
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** Tek çizgi popülerlik grafiği — düz SVG. */
const TimelineChart: React.FC<{ points: { period: string; searchPopularity1to100: number }[] }> = ({ points }) => {
  const width = 380;
  const height = 120;
  const values = points.map(p => p.searchPopularity1to100);
  const max = Math.max(...values, 100);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const stepX = width / (points.length - 1);
  const coords = points
    .map((p, i) => {
      const x = i * stepX;
      const y = height - ((p.searchPopularity1to100 - min) / span) * (height - 8) - 4;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" role="img" aria-label="popularity timeline">
      <polyline points={coords} fill="none" strokeWidth="2" className="stroke-blue-500" />
    </svg>
  );
};

export default ASOKeywordsPage;
